import React, {FC, PropsWithChildren} from 'react';
import Header from './header/Header';
import Breadcrumb from '@joyrideautos/web-common-components/src/layout/Breadcrumb';
import {Layout} from 'antd';
import GlobalAlertContainer from '@components/alerts/GlobalAlertContainer';
import NotificationComponent from '@components/notifications/NotificationComponent';
import {ZendeskWidgetView} from '@components/ZendeskWidget';
import {observer, Observer} from 'mobx-react-lite';
import useScrollToTop from '@hooks/useScrollToTop';
import {MainLayoutResizeDetectionContextProvider} from './MainLayoutResizeDetectionContextProvider';
import classNames from 'classnames';
import {useRootStore} from '@joyrideautos/ui-models/src/stores/rootStore/useRootStore';
import {Routes} from '../Routes';
import {useRouter} from '@joyrideautos/web-common-components/src/router/Router';
import {Only} from '@joyrideautos/ui-common/src/components/Only';
import {logError} from '@joyrideautos/ui-logger/src/utils';
import {RootStoreType} from '@models/stores/RootStore';
import ImpersonatedUserWarning from '@layouts/header/ImpersonateWarning';
import DeprecationBanner from '@joyrideautos/web-common-components/src/components/deprecationBanner/DeprecationBanner';
import MigrationBanner from '@joyrideautos/web-common-components/src/components/migrationBanner/MigrationBanner';

interface PortalPageTemplateProps extends PropsWithChildren {}

const PageTemplate: FC<PortalPageTemplateProps> = observer(({children}) => {
    const rootStore = useRootStore<RootStoreType>();
    const {presentationStore, authUserStore} = rootStore;
    useScrollToTop(presentationStore.needScrollToTop.value ?? true);
    const {replace} = useRouter();

    return (
        <div className="App">
            <NotificationComponent />
            <Observer>
                {() => (
                    <>
                        <ZendeskWidgetView
                            visible={presentationStore.liveChatVisible.value}
                            offset={{vertical: presentationStore.liveChatVerticalOffset.value}}
                        />
                        <MainLayoutResizeDetectionContextProvider
                            renderHeader={() => (
                                <>
                                    <Only when={authUserStore.loadingStatus.isReady}>
                                        {() => (
                                            <Header
                                                userInfo={authUserStore.userInfo}
                                                signOut={() => {
                                                    replace(Routes.SIGN_IN());
                                                    authUserStore.signOut().catch(logError());
                                                }}
                                                hideMenu={false}
                                                top={
                                                    authUserStore.userInfo?.adminEmail ? (
                                                        <ImpersonatedUserWarning />
                                                    ) : null
                                                }
                                                footer={<GlobalAlertContainer />}
                                            />
                                        )}
                                    </Only>
                                </>
                            )}
                            isFooterVisible={presentationStore.footerVisible.value}>
                            {rootStore.globalConfigStore.isMigrationModeEnabled.value ? (
                                <MigrationBanner
                                    plannedAvailabilityTs={rootStore.globalConfigStore.plannedAvailabilityTs.value}
                                />
                            ) : rootStore.globalConfigStore.isDeprecatedModeEnabled.value ? (
                                <DeprecationBanner
                                    mp3Url={rootStore.globalConfigStore.mp3Url.value}
                                    message="As part of our planned platform migration, your account has moved to our new site. Please visit the new site to sign in — the new platform is where you will go to buy and sell vehicles going forward."
                                />
                            ) : null}
                            <Observer>
                                {() => (
                                    <Layout.Content
                                        className={classNames({
                                            main: !presentationStore.isMobile.value,
                                            [presentationStore.containerClassName.value]:
                                                !presentationStore.containerClassName.isEmpty,
                                        })}>
                                        <Observer>
                                            {() => {
                                                return (
                                                    <Breadcrumb
                                                        sections={presentationStore.breadcrumb?.sections ?? []}
                                                    />
                                                );
                                            }}
                                        </Observer>
                                        {children}
                                    </Layout.Content>
                                )}
                            </Observer>
                        </MainLayoutResizeDetectionContextProvider>
                    </>
                )}
            </Observer>
        </div>
    );
});

export default PageTemplate;
